import { createContext, useContext, type HTMLAttributes } from "react"
import { Button, Table } from "antd"
import type { TableProps } from "antd"
import { HolderOutlined } from "@ant-design/icons"
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import classNames from "classnames"
import type { StaffTemplateRecord } from "./useVideoTemplates"
import styles from "./index.css"

type HandleState = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef"
> & { disabled: boolean }
const HandleContext = createContext<HandleState | null>(null)
const DisabledContext = createContext(false)
const VERTICAL_ONLY: Modifier = ({ transform }) => ({ ...transform, x: 0 })

export const TemplateDragHandle = () => {
  const handle = useContext(HandleContext)
  return (
    <Button
      type="text"
      className={styles.dragHandle}
      icon={<HolderOutlined />}
      ref={handle?.setActivatorNodeRef}
      {...handle?.attributes}
      {...handle?.listeners}
      aria-label="拖动模板排序"
      disabled={!handle || handle.disabled}
    />
  )
}

type SortableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  "data-row-key": string
}
const SortableRow = ({
  children,
  style,
  className,
  ...props
}: SortableRowProps) => {
  const disabled = useContext(DisabledContext)
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
    disabled,
  })
  return (
    <HandleContext.Provider
      value={{ attributes, listeners, setActivatorNodeRef, disabled }}
    >
      <tr
        {...props}
        ref={setNodeRef}
        className={classNames(className, isDragging && styles.draggingRow)}
        style={{
          ...style,
          transform: CSS.Translate.toString(transform),
          transition,
        }}
      >
        {children}
      </tr>
    </HandleContext.Provider>
  )
}

const TABLE_COMPONENTS = { body: { row: SortableRow } }
type SortableTemplateTableProps = Omit<
  TableProps<StaffTemplateRecord>,
  "components" | "rowKey" | "dataSource"
> & {
  records: StaffTemplateRecord[]
  disabled: boolean
  onReorder: (records: StaffTemplateRecord[]) => void
}

export const SortableTemplateTable = ({
  records,
  disabled,
  onReorder,
  ...props
}: SortableTemplateTableProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (disabled || !over || active.id === over.id) return
    const from = records.findIndex((record) => record.key === active.id)
    const to = records.findIndex((record) => record.key === over.id)
    if (from >= 0 && to >= 0) onReorder(arrayMove(records, from, to))
  }
  return (
    <DisabledContext.Provider value={disabled}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[VERTICAL_ONLY]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={records.map((record) => record.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table<StaffTemplateRecord>
            {...props}
            rowKey="key"
            components={TABLE_COMPONENTS}
            dataSource={records}
          />
        </SortableContext>
      </DndContext>
    </DisabledContext.Provider>
  )
}
